<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎤 Live Fingering Detection</h1>
        <p class="text-body-1 text-medium-emphasis">
            Play any note on your flute and see the fingering chart update in
            real-time
        </p>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Controls</h2>

                <div class="property-section bg-surface-light">
                    <button @click="toggleListening" class="listen-button">
                        {{ isListening ? "Stop Listening" : "Start Listening" }}
                    </button>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Fingering Size</h3>
                    <div class="property-control">
                        <label for="width-slider"
                            >Width: {{ fingeringWidth }}px</label
                        >
                        <input
                            id="width-slider"
                            type="range"
                            v-model.number="fingeringWidth"
                            min="300"
                            max="700"
                            step="50"
                        />
                    </div>
                    <div class="property-control">
                        <label for="height-slider"
                            >Height: {{ fingeringHeight }}px</label
                        >
                        <input
                            id="height-slider"
                            type="range"
                            v-model.number="fingeringHeight"
                            min="120"
                            max="300"
                            step="20"
                        />
                    </div>
                </div>
            </div>

            <!-- Center Panel: Fingering Display -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Live Fingering</h2>

                <div class="fingering-display">
                    <FluteFingering
                        :note="detectedNote || undefined"
                        :fingering="detectedFingering"
                        :width="fingeringWidth"
                        :height="fingeringHeight"
                        orientation="horizontal"
                    />
                </div>

                <div
                    v-if="!hasStandardFingering && detectedNote"
                    class="warning-message"
                >
                    <strong>⚠️ Note:</strong> {{ detectedNote }} does not have a
                    standard fingering in the chart
                </div>
            </div>

            <!-- Right Panel: Detection Info -->
            <div class="right-panel bg-surface">
                <h2 class="text-h5 text-secondary">Pitch Detection</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Status</h3>
                    <div class="info-display">
                        <div class="info-row">
                            <strong>Listening:</strong>
                            <span
                                :class="`status-${isListening ? 'active' : 'inactive'}`"
                            >
                                {{
                                    isListening ? "Yes" : "No"
                                }}
                            </span>
                        </div>
                        <div class="info-row">
                            <strong>Note:</strong>
                            <span class="note-value">{{
                                detectedNote || "—"
                            }}</span>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Analysis</h3>
                    <div class="info-display">
                        <div class="info-row">
                            <strong>Frequency:</strong>
                            <span>{{
                                detectedFrequency
                                    ? `${detectedFrequency.toFixed(2)} Hz`
                                    : "—"
                            }}</span>
                        </div>
                        <div class="info-row">
                            <strong>Confidence:</strong>
                            <span>{{
                                detectedConfidence
                                    ? `${(detectedConfidence * 100).toFixed(1)}%`
                                    : "—"
                            }}</span>
                        </div>
                        <div class="info-row">
                            <strong>Cents Off:</strong>
                            <span>{{
                                detectedCents !== null
                                    ? formatCents(detectedCents)
                                    : "—"
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Instructions -->
        <v-card class="instructions-section">
            <v-card-title class="text-h5 text-secondary">
                How to Use
            </v-card-title>
            <v-card-text>
                <ol class="usage-list">
                    <li>
                        Click <strong>"Start Listening"</strong> and grant
                        microphone permissions
                    </li>
                    <li>Play any note on your flute</li>
                    <li>
                        Watch the fingering chart update in real-time to show
                        the detected note's fingering
                    </li>
                    <li>
                        Use this to verify pitch detection accuracy and explore
                        fingering patterns
                    </li>
                </ol>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from "vue";
import FluteFingering from "../components/FluteFingering.vue";
import {
    getStandardFingering,
    hasStandardFingering as hasStandardFingeringChart,
    type FingeringState,
} from "../data/fluteFingeringChart";
import { createPitchDetector, DetectorType, analyzeFrequency } from "../pitch";

const isListening = ref(false);
const detectedNote = ref<string | null>(null);
const detectedFrequency = ref<number | null>(null);
const detectedConfidence = ref<number | null>(null);
const detectedCents = ref<number | null>(null);
const fingeringWidth = ref(500);
const fingeringHeight = ref(180);

// Audio state
const audioContext = ref<AudioContext | null>(null);
const analyser = ref<AnalyserNode | null>(null);
const mediaStream = ref<MediaStream | null>(null);
const detector = createPitchDetector(DetectorType.BLUESHARP, "flute");
const animationFrameId = ref<number | null>(null);

const detectedFingering = computed(() => {
    if (!detectedNote.value) return {};
    return getStandardFingering(detectedNote.value) || {};
});

const hasStandardFingering = computed(() => {
    if (!detectedNote.value) return true;
    return hasStandardFingeringChart(detectedNote.value);
});

function formatCents(cents: number): string {
    const sign = cents > 0 ? "+" : "";
    return `${sign}${cents.toFixed(0)}¢`;
}

async function startListening() {
    try {
        mediaStream.value = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
            },
        });

        audioContext.value = new AudioContext();
        const source = audioContext.value.createMediaStreamSource(
            mediaStream.value,
        );

        analyser.value = audioContext.value.createAnalyser();
        analyser.value.fftSize = 2048;
        analyser.value.smoothingTimeConstant = 0.8;
        source.connect(analyser.value);

        isListening.value = true;
        analyzePitch();
    } catch (error) {
        console.error("Failed to access microphone:", error);
        alert("Could not access microphone. Please check permissions.");
    }
}

function stopListening() {
    isListening.value = false;
    detectedNote.value = null;
    detectedFrequency.value = null;
    detectedConfidence.value = null;
    detectedCents.value = null;

    if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
    }

    if (mediaStream.value) {
        mediaStream.value
            .getTracks()
            .forEach((track: MediaStreamTrack) => track.stop());
        mediaStream.value = null;
    }

    if (audioContext.value) {
        audioContext.value.close();
        audioContext.value = null;
    }

    analyser.value = null;
}

function toggleListening() {
    if (isListening.value) {
        stopListening();
    } else {
        startListening();
    }
}

function analyzePitch() {
    if (!isListening.value || !analyser.value || !audioContext.value) {
        return;
    }

    const buffer = new Float32Array(analyser.value.frequencyBinCount);
    analyser.value.getFloatTimeDomainData(buffer);

    const result = detector.detectPitch(buffer, audioContext.value.sampleRate);

    if (result.frequency) {
        const analysis = analyzeFrequency(result.frequency);

        detectedNote.value = analysis.noteName;
        detectedFrequency.value = result.frequency;
        detectedConfidence.value = result.confidence;
        detectedCents.value = analysis.centsOff;
    } else {
        detectedNote.value = null;
        detectedFrequency.value = null;
        detectedConfidence.value = null;
        detectedCents.value = null;
    }

    animationFrameId.value = requestAnimationFrame(analyzePitch);
}

onUnmounted(() => {
    stopListening();
});
</script>

<style scoped>
.debug-container {
    width: 100%;
    margin: 0;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 1fr 1.5fr 1fr;
    gap: 20px;
    height: calc(100vh - 200px);
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.right-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.property-control input[type="range"] {
    width: 100%;
}

.listen-button {
    width: 100%;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    color: white;
    background: #007bff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.listen-button:hover {
    background: #0056b3;
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.4);
    transform: translateY(-1px);
}

.info-display {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    padding: 8px;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 4px;
    font-size: 14px;
}

.status-active {
    color: #28a745;
    font-weight: 600;
}

.status-inactive {
    color: #6c757d;
}

.note-value {
    font-size: 18px;
    font-weight: 700;
    color: #007bff;
}

.placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #6c757d;
    text-align: center;
}

.placeholder-icon {
    font-size: 72px;
    margin-bottom: 20px;
    opacity: 0.3;
}

.placeholder p {
    font-size: 18px;
}

.fingering-display {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    position: relative;
    flex: 1;
}

.status-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(255, 255, 255, 0.9);
    padding: 20px;
    border-radius: 8px;
    text-align: center;
    color: #6c757d;
    font-size: 18px;
    pointer-events: none;
    backdrop-filter: blur(2px);
}

.warning-message {
    margin-top: 20px;
    padding: 12px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    color: #856404;
    font-size: 14px;
}

.instructions-section {
    display: none;
}

.usage-list {
    line-height: 1.8;
    padding-left: 20px;
}

.usage-list li {
    margin-bottom: 12px;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
